import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { formatCurrencyWithCommas } from '@/utils/formatNumber';
import { type SortOption } from '@elements/filters/SortByDropdown';
import type { CurrentPlanFormData } from '@/components/elements/CompareCurrentPlanModal';
import type { PlanWithAgePremiums } from './types';
import { buildShopPlansStateRootPath } from '@/lib/shopPlansRouting';

type FiltersState = {
    metal: string[];
    planMarket: string[];
    enrollment: string[];
    carriers: string[];
    features: string[];
    premiumMin?: number;
    premiumMax?: number;
    deductibleMin?: number;
    deductibleMax?: number;
    maxOutOfPocketMin?: number;
    maxOutOfPocketMax?: number;
};

export function usePlansSectionState({
    plans,
    isStateCountrySearch,
    familyAges,
    activeTab,
}: {
    plans: PlanWithAgePremiums[];
    isStateCountrySearch: boolean;
    familyAges: string[];
    activeTab?: string;
}) {
    const CURRENT_PLAN_PARAM = 'currentPlan';
    const EMPLOYER_CONTRIBUTION_PARAM = 'employerContribution';

    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const isHydratingFromUrlRef = useRef(true);
    const hasHydratedCompareFromUrlRef = useRef(false);
    const isUpdatingCompareFromUrlRef = useRef(false);
    const hasHydratedCurrentPlanFromUrlRef = useRef(false);
    const isUpdatingCurrentPlanFromUrlRef = useRef(false);
    const hasHydratedEmployerContributionFromUrlRef = useRef(false);
    const isUpdatingEmployerContributionFromUrlRef = useRef(false);
    const comparePlansRef = useRef<PlanWithAgePremiums[]>([]);

    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [sortBy, setSortBy] = useState<SortOption | undefined>(() => {
        const urlSortBy = searchParams.get('sortBy');
        if (urlSortBy && ['lowest-premium', 'lowest-deductible', 'lowest-max-out-of-pocket'].includes(urlSortBy)) {
            return urlSortBy as SortOption;
        }
        return 'lowest-premium';
    });

    const [isFiltersModalOpen, setIsFiltersModalOpen] = useState<boolean>(false);
    const [comparePlans, setComparePlans] = useState<PlanWithAgePremiums[]>([]);
    const [isCompareModalOpen, setIsCompareModalOpen] = useState<boolean>(false);
    const [isCurrentPlanModalOpen, setIsCurrentPlanModalOpen] = useState<boolean>(false);
    const [isCompareCurrentPlanLimitModalOpen, setIsCompareCurrentPlanLimitModalOpen] = useState<boolean>(false);
    const [isComparePlansExpanded, setIsComparePlansExpanded] = useState<boolean>(false);
    const [isEmployerContributionExpanded, setIsEmployerContributionExpanded] = useState<boolean>(false);
    const prevComparePlansCountRef = useRef<number>(0);

    const [employerContributionInput, setEmployerContributionInput] = useState<string>('');
    const [employerContributionAmount, setEmployerContributionAmount] = useState<number | null>(null);

    const compareIdsFromPlans = useCallback(
        (items: PlanWithAgePremiums[]) =>
            items
                .filter((plan) => !(plan as any).isCurrentPlan)
                .map((plan) => plan.id)
                .filter(Boolean),
        [],
    );

    const areSameIds = useCallback((left: string[], right: string[]) => {
        if (left.length !== right.length) return false;
        return left.every((id, index) => id === right[index]);
    }, []);

    const parseMoney = useCallback((value: string): number => {
        const cleaned = String(value ?? '').replace(/[^0-9.]/g, '');
        const n = parseFloat(cleaned);
        return Number.isFinite(n) ? n : 0;
    }, []);

    const formatMoneyInput = useCallback((raw: string): string => {
        const cleaned = String(raw ?? '').replace(/[^\d.]/g, '');
        if (!cleaned) return '';
        const [integerPart, decimalPart] = cleaned.split('.');
        const integerFormatted = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        if (decimalPart == null) return integerFormatted;
        const trimmedDecimals = decimalPart.slice(0, 2);
        return `${integerFormatted}.${trimmedDecimals}`;
    }, []);

    const formatMoneyFixed2 = useCallback((n: number): string => {
        if (!Number.isFinite(n)) return '';
        return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
    }, []);

    const serializeEmployerContributionToUrlParam = useCallback((amount: number | null): string => {
        if (amount == null) return '';
        if (!Number.isFinite(amount) || amount <= 0) return '';
        return amount.toFixed(2);
    }, []);

    const parseCurrentPlanFromUrlParam = useCallback((param: string | null): PlanWithAgePremiums | null => {
        if (!param) return null;
        try {
            const payload = JSON.parse(param) as {
                p?: number;
                d?: number;
                m?: number;
                c?: string;
                n?: string;
                l?: string;
            };

            const premiumNum = Number.isFinite(payload.p) ? Number(payload.p) : 0;
            const deductibleNum = Number.isFinite(payload.d) ? Number(payload.d) : 0;
            const moopNum = Number.isFinite(payload.m) ? Number(payload.m) : 0;
            const carrierName = String(payload.c ?? '').trim() || 'Current Carrier';
            const planDisplayName = String(payload.n ?? '').trim() || 'Current Plan';
            const level = String(payload.l ?? '').trim() || 'Silver';

            const currentPlan: PlanWithAgePremiums = {
                id: 'current-plan',
                name: planDisplayName,
                display_name: planDisplayName,
                type: 'current',
                level,
                plan_type: 'Current Plan',
                effective_date: '',
                expiration_date: '',
                on_market: false,
                off_market: false,
                premium: premiumNum > 0 ? formatCurrencyWithCommas(premiumNum) : '',
                carrier: {
                    id: 'current',
                    name: carrierName,
                    name_long: carrierName,
                    issuer_id: 'current',
                    logo_url: null,
                },
                identifiers: [],
                benefits_summary_url: null,
                drug_formulary_url: null,
                network_provider_directory_url: null,
                predictable_benefits_plan_url: null,
                individual_medical_deductible: deductibleNum > 0 ? formatCurrencyWithCommas(deductibleNum) : '',
                family_medical_deductible: deductibleNum > 0 ? formatCurrencyWithCommas(deductibleNum) : '',
                individual_medical_moop: moopNum > 0 ? formatCurrencyWithCommas(moopNum) : '',
                family_medical_moop: moopNum > 0 ? formatCurrencyWithCommas(moopNum) : '',
                deductible_numerical: deductibleNum,
                max_out_of_pocket_numerical: moopNum,
                preventitive_numerical: 0,
                primary_care_numerical: 0,
                specelist_numerical: 0,
                emergency_numerical: 0,
                urgent_care_numerical: 0,
                telemedicine: null,
                telemedicine_eligible: false,
                gated: false,
                hsa_eligible: false,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ...({
                    isCurrentPlan: true,
                    deductibles: {
                        individual_medical: deductibleNum || undefined,
                        family_medical: deductibleNum || undefined,
                        individual_drug: deductibleNum || undefined,
                        family_drug: deductibleNum || undefined,
                    },
                    moop: {
                        individual: moopNum || undefined,
                        family: moopNum || undefined,
                    },
                } as any),
            };

            return currentPlan;
        } catch {
            return null;
        }
    }, []);

    const serializeCurrentPlanToUrlParam = useCallback(
        (plan: PlanWithAgePremiums): string => {
            const carrierName = String(plan.carrier?.name_long ?? plan.carrier?.name ?? '').trim();
            const planName = String(plan.display_name ?? plan.name ?? '').trim();
            const level = String((plan as any).level ?? '').trim();
            const premiumNum = parseMoney(String((plan as any).premium ?? ''));
            const deductibleNum =
                typeof (plan as any).deductible_numerical === 'number'
                    ? (plan as any).deductible_numerical
                    : parseMoney(String((plan as any).individual_medical_deductible ?? ''));
            const moopNum =
                typeof (plan as any).max_out_of_pocket_numerical === 'number'
                    ? (plan as any).max_out_of_pocket_numerical
                    : parseMoney(String((plan as any).individual_medical_moop ?? ''));

            return JSON.stringify({
                p: premiumNum || 0,
                d: deductibleNum || 0,
                m: moopNum || 0,
                c: carrierName,
                n: planName,
                l: level,
            });
        },
        [parseMoney],
    );

    const DEFAULT_METAL = useMemo(() => ['Catastrophic', 'Bronze', 'Silver', 'Gold', 'Platinum'], []);
    const DEFAULT_PLAN_MARKET = useMemo(() => ['On-Exchange', 'Off-Exchange'], []);

    const normalizeStringList = useCallback((values: string[]) => {
        const unique = new Map<string, string>();
        values
            .map((v) => String(v).trim())
            .filter(Boolean)
            .forEach((v) => {
                const key = v.toLowerCase();
                if (!unique.has(key)) unique.set(key, v);
            });
        return Array.from(unique.values());
    }, []);

    const sameSet = useCallback((a: string[], b: string[]) => {
        if (a.length !== b.length) return false;
        const setB = new Set(b);
        return a.every((x) => setB.has(x));
    }, []);

    const toTitleCase = useCallback((input?: string) => {
        const s = (input ?? '').trim();
        if (!s) return '';
        return s
            .replace(/\s+/g, ' ')
            .split(' ')
            .map((word) => {
                const lower = word.toLowerCase();
                return lower ? lower[0].toUpperCase() + lower.slice(1) : lower;
            })
            .join(' ');
    }, []);

    const allCarriers = useMemo(() => {
        const unique = new Set<string>();
        plans.forEach((p) => {
            if (p.carrier?.name) unique.add(p.carrier.name);
        });
        return Array.from(unique).sort();
    }, [plans]);

    const carrierLookup = useMemo(() => {
        const byShort = new Map<string, string>();
        const byLong = new Map<string, string>();
        plans.forEach((p) => {
            const short = p.carrier?.name;
            if (short) {
                byShort.set(short.toLowerCase(), short);
                const long = p.carrier?.name_long;
                if (long) byLong.set(long.toLowerCase(), short);
            }
        });
        return { byShort, byLong };
    }, [plans]);

    const computeFiltersKey = useCallback((f: FiltersState) => {
        return JSON.stringify({
            metal: [...f.metal].sort(),
            planMarket: [...f.planMarket].sort(),
            enrollment: [...f.enrollment].sort(),
            carriers: [...f.carriers].sort(),
            features: [...f.features].sort(),
            premiumMin: f.premiumMin ?? null,
            premiumMax: f.premiumMax ?? null,
            deductibleMin: f.deductibleMin ?? null,
            deductibleMax: f.deductibleMax ?? null,
            maxOutOfPocketMin: f.maxOutOfPocketMin ?? null,
            maxOutOfPocketMax: f.maxOutOfPocketMax ?? null,
        });
    }, []);

    const parseNumber = useCallback((v: string | null) => {
        if (!v) return undefined;
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : undefined;
    }, []);

    const parseFiltersFromUrl = useCallback(
        (sp: URLSearchParams) => {
            const metalRaw = normalizeStringList(sp.getAll('metal'));
            const planMarketRaw = normalizeStringList(sp.getAll('planMarket'));
            const urlCarrierTokens = normalizeStringList(sp.getAll('carriers'));
            const featuresRaw = normalizeStringList(sp.getAll('features'));

            const carriersInUrl = urlCarrierTokens.length > 0;

            const carriersNormalized = carriersInUrl
                ? urlCarrierTokens
                      .map((token) => {
                          const lower = token.toLowerCase();
                          return carrierLookup.byShort.get(lower) ?? carrierLookup.byLong.get(lower) ?? token;
                      })
                      .filter(Boolean)
                : allCarriers.length > 0
                  ? allCarriers
                  : [];

            const next: FiltersState = {
                metal: metalRaw.length > 0 ? metalRaw : DEFAULT_METAL,
                planMarket: planMarketRaw.length > 0 ? planMarketRaw : DEFAULT_PLAN_MARKET,
                enrollment: [],
                carriers: carriersNormalized,
                features: featuresRaw.length > 0 ? featuresRaw : [],
                premiumMin: parseNumber(sp.get('premiumMin')),
                premiumMax: parseNumber(sp.get('premiumMax')),
                deductibleMin: parseNumber(sp.get('deductibleMin')),
                deductibleMax: parseNumber(sp.get('deductibleMax')),
                maxOutOfPocketMin: parseNumber(sp.get('maxOutOfPocketMin')),
                maxOutOfPocketMax: parseNumber(sp.get('maxOutOfPocketMax')),
            };

            return { filters: next, meta: { carriersInUrl, urlCarrierTokens } };
        },
        [
            DEFAULT_METAL,
            DEFAULT_PLAN_MARKET,
            allCarriers,
            carrierLookup.byLong,
            carrierLookup.byShort,
            normalizeStringList,
            parseNumber,
        ],
    );

    const [filters, setFilters] = useState<FiltersState>(() => parseFiltersFromUrl(searchParams).filters);

    useEffect(() => {
        const { filters: nextFilters } = parseFiltersFromUrl(searchParams);
        setFilters((prev) => (computeFiltersKey(prev) === computeFiltersKey(nextFilters) ? prev : nextFilters));
        if (isHydratingFromUrlRef.current) isHydratingFromUrlRef.current = false;
    }, [computeFiltersKey, parseFiltersFromUrl, searchParams]);

    useEffect(() => {
        const urlSortBy = searchParams.get('sortBy');
        if (urlSortBy && ['lowest-premium', 'lowest-deductible', 'lowest-max-out-of-pocket'].includes(urlSortBy)) {
            setSortBy(urlSortBy as SortOption);
        } else if (!urlSortBy) {
            setSortBy('lowest-premium');
        }
    }, [searchParams]);

    // Compare plans URL sync: hydrate compare plans from URL (compare=id1,id2,id3)
    useEffect(() => {
        if (plans.length === 0) return;

        const compareParam = searchParams.get('compare');
        if (!compareParam) {
            hasHydratedCompareFromUrlRef.current = true;
            return;
        }

        const idsFromUrl = compareParam
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean)
            .slice(0, 3);

        const nextPlans = idsFromUrl
            .map((id) => plans.find((plan) => plan.id === id))
            .filter(Boolean) as PlanWithAgePremiums[];

        const nextIds = compareIdsFromPlans(nextPlans);
        const currentIds = compareIdsFromPlans(comparePlansRef.current);

        if (areSameIds(nextIds, currentIds)) {
            hasHydratedCompareFromUrlRef.current = true;
            return;
        }

        const currentPlanFromUrl = parseCurrentPlanFromUrlParam(searchParams.get(CURRENT_PLAN_PARAM));
        const nextWithCurrent = currentPlanFromUrl ? [...nextPlans, currentPlanFromUrl] : nextPlans;

        isUpdatingCompareFromUrlRef.current = true;
        setComparePlans(nextWithCurrent);
        hasHydratedCompareFromUrlRef.current = true;
    }, [plans, searchParams, compareIdsFromPlans, areSameIds, parseCurrentPlanFromUrlParam]);

    useEffect(() => {
        comparePlansRef.current = comparePlans;
    }, [comparePlans]);

    // Compare Plans block auto-expand/collapse behavior
    useEffect(() => {
        const prev = prevComparePlansCountRef.current;
        const next = comparePlans.length;
        if (prev === 0 && next > 0) setIsComparePlansExpanded(true);
        if (next === 0) setIsComparePlansExpanded(false);
        prevComparePlansCountRef.current = next;
    }, [comparePlans.length]);

    // Current plan URL sync: hydrate current plan from URL
    useEffect(() => {
        const currentParam = searchParams.get(CURRENT_PLAN_PARAM);

        if (!currentParam) {
            hasHydratedCurrentPlanFromUrlRef.current = true;
            if (comparePlansRef.current.some((p) => (p as any).isCurrentPlan)) {
                isUpdatingCurrentPlanFromUrlRef.current = true;
                setComparePlans((prev) => prev.filter((p) => !(p as any).isCurrentPlan));
            }
            return;
        }

        const currentPlanFromUrl = parseCurrentPlanFromUrlParam(currentParam);
        hasHydratedCurrentPlanFromUrlRef.current = true;
        if (!currentPlanFromUrl) return;

        isUpdatingCurrentPlanFromUrlRef.current = true;
        setComparePlans((prev) => {
            const withoutCurrent = prev.filter((p) => !(p as any).isCurrentPlan && p.id !== 'current-plan');
            return [...withoutCurrent, currentPlanFromUrl];
        });
    }, [searchParams, parseCurrentPlanFromUrlParam]);

    // Employer contribution URL sync: hydrate from URL
    useEffect(() => {
        const param = searchParams.get(EMPLOYER_CONTRIBUTION_PARAM);
        hasHydratedEmployerContributionFromUrlRef.current = true;

        if (!param) {
            isUpdatingEmployerContributionFromUrlRef.current = true;
            setEmployerContributionAmount(null);
            setEmployerContributionInput('');
            return;
        }

        const amount = parseMoney(param);
        if (!(amount > 0)) {
            isUpdatingEmployerContributionFromUrlRef.current = true;
            setEmployerContributionAmount(null);
            setEmployerContributionInput('');
            return;
        }

        const nextInput = formatMoneyFixed2(amount);
        isUpdatingEmployerContributionFromUrlRef.current = true;
        setEmployerContributionAmount(amount);
        setEmployerContributionInput(nextInput);
    }, [formatMoneyFixed2, parseMoney, searchParams]);

    // Write compare/current plan params when compare selection changes
    useEffect(() => {
        const params = new URLSearchParams(searchParams);
        let changed = false;

        if (!hasHydratedCurrentPlanFromUrlRef.current) return;

        if (isUpdatingCompareFromUrlRef.current) {
            isUpdatingCompareFromUrlRef.current = false;
            return;
        }
        if (isUpdatingCurrentPlanFromUrlRef.current) {
            isUpdatingCurrentPlanFromUrlRef.current = false;
            return;
        }

        if (hasHydratedCompareFromUrlRef.current) {
            const ids = compareIdsFromPlans(comparePlans).slice(0, 3);
            const nextCompareValue = ids.join(',');
            const currentCompareValue = searchParams.get('compare') || '';

            if (nextCompareValue !== currentCompareValue) {
                if (ids.length === 0) params.delete('compare');
                else params.set('compare', nextCompareValue);
                changed = true;
            }
        }

        const currentPlan = comparePlans.find((p) => (p as any).isCurrentPlan) as PlanWithAgePremiums | undefined;
        const nextCurrentValue = currentPlan ? serializeCurrentPlanToUrlParam(currentPlan) : '';
        const currentCurrentValue = searchParams.get(CURRENT_PLAN_PARAM) || '';

        if (nextCurrentValue !== currentCurrentValue) {
            if (!currentPlan) params.delete(CURRENT_PLAN_PARAM);
            else params.set(CURRENT_PLAN_PARAM, nextCurrentValue);
            changed = true;
        }

        if (!changed) return;
        setSearchParams(params, { replace: true, preventScrollReset: true });
    }, [comparePlans, compareIdsFromPlans, searchParams, serializeCurrentPlanToUrlParam, setSearchParams]);

    // Write employerContribution when value changes
    useEffect(() => {
        if (!hasHydratedEmployerContributionFromUrlRef.current) return;

        if (isUpdatingEmployerContributionFromUrlRef.current) {
            isUpdatingEmployerContributionFromUrlRef.current = false;
            return;
        }

        const params = new URLSearchParams(searchParams);
        const nextValue = serializeEmployerContributionToUrlParam(employerContributionAmount);
        const currentValue = searchParams.get(EMPLOYER_CONTRIBUTION_PARAM) || '';

        if (nextValue === currentValue) return;

        if (!nextValue) params.delete(EMPLOYER_CONTRIBUTION_PARAM);
        else params.set(EMPLOYER_CONTRIBUTION_PARAM, nextValue);

        setSearchParams(params, { replace: true, preventScrollReset: true });
    }, [
        EMPLOYER_CONTRIBUTION_PARAM,
        employerContributionAmount,
        searchParams,
        serializeEmployerContributionToUrlParam,
        setSearchParams,
    ]);

    // Validate URL carriers against API response; remove invalid ones
    const hasValidatedUrlCarriersRef = useRef(false);
    useEffect(() => {
        if (hasValidatedUrlCarriersRef.current) return;
        if (plans.length === 0) return;

        const rawTokens = normalizeStringList(searchParams.getAll('carriers'));
        if (rawTokens.length === 0) {
            hasValidatedUrlCarriersRef.current = true;
            return;
        }

        const known = new Set<string>([
            ...Array.from(carrierLookup.byShort.keys()),
            ...Array.from(carrierLookup.byLong.keys()),
        ]);

        const validTokens = rawTokens.filter((t) => known.has(t.toLowerCase()));
        if (validTokens.length === rawTokens.length) {
            hasValidatedUrlCarriersRef.current = true;
            return;
        }

        hasValidatedUrlCarriersRef.current = true;
        const params = new URLSearchParams(searchParams);
        params.delete('carriers');
        validTokens.forEach((t) => params.append('carriers', t));
        setSearchParams(params, { replace: true, preventScrollReset: true });
    }, [carrierLookup.byLong, carrierLookup.byShort, normalizeStringList, plans.length, searchParams, setSearchParams]);

    const writeFiltersToUrl = useCallback(
        (nextFilters: FiltersState) => {
            if (isHydratingFromUrlRef.current) return;

            const params = new URLSearchParams(searchParams);

            params.delete('metal');
            params.delete('planMarket');
            params.delete('carriers');
            params.delete('features');
            params.delete('premiumMin');
            params.delete('premiumMax');
            params.delete('deductibleMin');
            params.delete('deductibleMax');
            params.delete('maxOutOfPocketMin');
            params.delete('maxOutOfPocketMax');

            const metal = normalizeStringList(nextFilters.metal);
            const planMarket = normalizeStringList(nextFilters.planMarket);
            const carriers = normalizeStringList(nextFilters.carriers);
            const features = normalizeStringList(nextFilters.features);

            if (!sameSet(metal, DEFAULT_METAL)) metal.forEach((m) => params.append('metal', m));
            if (!sameSet(planMarket, DEFAULT_PLAN_MARKET)) planMarket.forEach((m) => params.append('planMarket', m));

            const carriersAreDefault =
                allCarriers.length > 0 &&
                carriers.length === allCarriers.length &&
                carriers.every((c) => allCarriers.includes(c));

            if (!carriersAreDefault) carriers.forEach((c) => params.append('carriers', c));
            if (features.length > 0) features.forEach((f) => params.append('features', f));

            if (nextFilters.premiumMin !== undefined) params.set('premiumMin', String(nextFilters.premiumMin));
            if (nextFilters.premiumMax !== undefined) params.set('premiumMax', String(nextFilters.premiumMax));
            if (nextFilters.deductibleMin !== undefined) params.set('deductibleMin', String(nextFilters.deductibleMin));
            if (nextFilters.deductibleMax !== undefined) params.set('deductibleMax', String(nextFilters.deductibleMax));
            if (nextFilters.maxOutOfPocketMin !== undefined) params.set('maxOutOfPocketMin', String(nextFilters.maxOutOfPocketMin));
            if (nextFilters.maxOutOfPocketMax !== undefined) params.set('maxOutOfPocketMax', String(nextFilters.maxOutOfPocketMax));

            if (params.toString() !== searchParams.toString()) {
                setSearchParams(params, { replace: true, preventScrollReset: true });
            }
        },
        [
            DEFAULT_METAL,
            DEFAULT_PLAN_MARKET,
            allCarriers,
            normalizeStringList,
            sameSet,
            searchParams,
            setSearchParams,
        ],
    );

    const filteredPlans = useMemo(() => {
        const parsePremium = (premium: string | undefined): number => {
            if (!premium) return Infinity;
            const premiumStr = String(premium).trim();
            const cleanedPremium = premiumStr.replace(/[^0-9.]/g, '');
            const planPremium = parseFloat(cleanedPremium);
            return isNaN(planPremium) ? Infinity : planPremium;
        };

        const allUniqueCarriers = new Set<string>();
        plans.forEach((plan) => {
            if (plan.carrier?.name) allUniqueCarriers.add(plan.carrier.name);
        });
        const allCarriersArray = Array.from(allUniqueCarriers).sort();
        const allCarriersSelected =
            filters.carriers.length > 0 &&
            filters.carriers.length === allCarriersArray.length &&
            filters.carriers.every((carrier) => allCarriersArray.includes(carrier));

        const filtered = plans.filter((plan) => {
            if (filters.metal.length === 0) return false;
            if (filters.metal.length > 0 && filters.metal.length < 5) {
                const planLevel = plan.level.toLowerCase();
                const matchesMetal = filters.metal.some((metal) => planLevel.includes(metal.toLowerCase()));
                if (!matchesMetal) return false;
            }

            if (filters.planMarket.length === 0) return false;
            if (filters.planMarket.length > 0 && filters.planMarket.length < 2) {
                const isOnExchange = plan.on_market === true;
                const isOffExchange = plan.off_market === true;
                const matchesMarket = filters.planMarket.some((market) => {
                    if (market === 'On-Exchange') return isOnExchange;
                    if (market === 'Off-Exchange') return isOffExchange;
                    return false;
                });
                if (!matchesMarket) return false;
            }

            if (filters.carriers.length === 0) return false;
            if (filters.carriers.length > 0 && !allCarriersSelected) {
                const planCarrierName = plan.carrier.name.trim();
                const matchesCarrier = filters.carriers.some((carrier) => {
                    const filterCarrierName = carrier.trim();
                    return planCarrierName.toLowerCase() === filterCarrierName.toLowerCase();
                });
                if (!matchesCarrier) return false;
            }

            if (filters.features.length > 0) {
                if (filters.features.includes('HSA Eligible') && !plan.hsa_eligible) return false;
                if (filters.features.includes('Specialist Without Referral') && plan.gated === true) return false;
            }

            if (filters.premiumMin !== undefined || filters.premiumMax !== undefined) {
                if (!plan.premium) return false;
                const premiumStr = String(plan.premium).trim();
                const cleanedPremium = premiumStr.replace(/[^0-9.]/g, '');
                const planPremium = parseFloat(cleanedPremium);
                if (isNaN(planPremium)) return false;
                if (filters.premiumMin !== undefined && planPremium < filters.premiumMin) return false;
                if (filters.premiumMax !== undefined && planPremium > filters.premiumMax) return false;
            }

            if (filters.deductibleMin !== undefined || filters.deductibleMax !== undefined) {
                const planDeductible = plan.deductible_numerical;
                if (planDeductible === undefined || planDeductible === null || isNaN(planDeductible)) return false;
                if (filters.deductibleMin !== undefined && planDeductible < filters.deductibleMin) return false;
                if (filters.deductibleMax !== undefined && planDeductible > filters.deductibleMax) return false;
            }

            if (filters.maxOutOfPocketMin !== undefined || filters.maxOutOfPocketMax !== undefined) {
                const planMaxOutOfPocket = plan.max_out_of_pocket_numerical;
                if (planMaxOutOfPocket === undefined || planMaxOutOfPocket === null || isNaN(planMaxOutOfPocket)) return false;
                if (filters.maxOutOfPocketMin !== undefined && planMaxOutOfPocket < filters.maxOutOfPocketMin) return false;
                if (filters.maxOutOfPocketMax !== undefined && planMaxOutOfPocket > filters.maxOutOfPocketMax) return false;
            }

            return true;
        });

        if (sortBy === 'lowest-premium') {
            return filtered.sort((a, b) => parsePremium(a.premium) - parsePremium(b.premium));
        }
        if (sortBy === 'lowest-deductible') {
            return filtered.sort((a, b) => (a.deductible_numerical ?? Infinity) - (b.deductible_numerical ?? Infinity));
        }
        if (sortBy === 'lowest-max-out-of-pocket') {
            return filtered.sort((a, b) => (a.max_out_of_pocket_numerical ?? Infinity) - (b.max_out_of_pocket_numerical ?? Infinity));
        }

        const getMetalLevelPriority = (level: string): number => {
            const levelLower = level.toLowerCase();
            if (levelLower.includes('catastrophic')) return 0;
            if (levelLower.includes('bronze')) return 1;
            if (levelLower.includes('silver')) return 2;
            if (levelLower.includes('gold')) return 3;
            if (levelLower.includes('platinum')) return 4;
            return 5;
        };

        return filtered.sort((a, b) => {
            const priorityA = getMetalLevelPriority(a.level);
            const priorityB = getMetalLevelPriority(b.level);
            if (priorityA !== priorityB) return priorityA - priorityB;
            return parsePremium(a.premium) - parsePremium(b.premium);
        });
    }, [plans, filters, sortBy]);

    const summaryData = useMemo(() => {
        const levels = ['Catastrophic', 'Bronze', 'Silver', 'Gold', 'Platinum'] as const;
        const summary: { average: Record<string, string>; lowest: Record<string, string> } = {
            average: {},
            lowest: {},
        };

        levels.forEach((level) => {
            const levelPlans = filteredPlans.filter((p) => p.level.toLowerCase().includes(level.toLowerCase()));
            if (levelPlans.length > 0) {
                const premiums = levelPlans
                    .map((p) => {
                        const premiumStr = p.premium != null ? String(p.premium) : '';
                        return parseFloat(premiumStr.replace(/[^0-9.]/g, '')) || 0;
                    })
                    .filter((p) => p > 0);
                if (premiums.length > 0) {
                    const average = premiums.reduce((a, b) => a + b, 0) / premiums.length;
                    const lowest = Math.min(...premiums);
                    summary.average[level] = formatCurrencyWithCommas(average);
                    summary.lowest[level] = formatCurrencyWithCommas(lowest);
                } else {
                    summary.average[level] = '-';
                    summary.lowest[level] = '-';
                }
            } else {
                summary.average[level] = '-';
                summary.lowest[level] = '-';
            }
        });

        return summary;
    }, [filteredPlans]);

    const totalPages = useMemo(() => Math.max(1, Math.ceil(filteredPlans.length / pageSize) || 1), [filteredPlans.length, pageSize]);

    useEffect(() => {
        setPage(1);
    }, [filters]);

    const handleFiltersPanelChange = useCallback(
        (next: FiltersState) => {
            const fixed: FiltersState = {
                ...next,
                metal: next.metal.length > 0 ? next.metal : DEFAULT_METAL,
                planMarket: next.planMarket.length > 0 ? next.planMarket : DEFAULT_PLAN_MARKET,
                carriers: next.carriers.length > 0 ? next.carriers : allCarriers.length > 0 ? allCarriers : [],
            };
            setFilters(fixed);
            writeFiltersToUrl(fixed);
            setPage(1);
        },
        [DEFAULT_METAL, DEFAULT_PLAN_MARKET, allCarriers, writeFiltersToUrl],
    );

    const paginatedPlans = useMemo(() => {
        const start = (page - 1) * pageSize;
        const end = start + pageSize;
        return filteredPlans.slice(start, end);
    }, [filteredPlans, page, pageSize]);

    const handleToggleComparePlan = useCallback((plan: PlanWithAgePremiums) => {
        setComparePlans((prev) => {
            const exists = prev.some((p) => p.id === plan.id);
            if (exists) return prev.filter((p) => p.id !== plan.id);
            if (prev.length >= 3) return prev;
            return [...prev, plan];
        });
    }, []);

    const handleRemoveComparePlan = useCallback((planId: string) => {
        setComparePlans((prev) => prev.filter((p) => p.id !== planId));
    }, []);

    const handleAddCurrentPlanToCompare = useCallback(
        (data: CurrentPlanFormData) => {
            const premiumNum = parseMoney(data.monthlyPremium);
            const deductibleNum = parseMoney(data.deductible);
            const moopNum = parseMoney(data.maxOutOfPocket);

            const carrierName = data.carrier.trim() || 'Current Carrier';
            const planDisplayName = data.planName.trim() || 'Current Plan';
            const level = data.metalLevel || 'Silver';

            const currentPlan: PlanWithAgePremiums = {
                id: 'current-plan',
                name: planDisplayName,
                display_name: planDisplayName,
                type: 'current',
                level,
                plan_type: 'Current Plan',
                effective_date: '',
                expiration_date: '',
                on_market: false,
                off_market: false,
                premium: premiumNum > 0 ? formatCurrencyWithCommas(premiumNum) : String(data.monthlyPremium || ''),
                carrier: {
                    id: 'current',
                    name: carrierName,
                    name_long: carrierName,
                    issuer_id: 'current',
                    logo_url: null,
                },
                identifiers: [],
                benefits_summary_url: null,
                drug_formulary_url: null,
                network_provider_directory_url: null,
                predictable_benefits_plan_url: null,
                individual_medical_deductible: deductibleNum > 0 ? formatCurrencyWithCommas(deductibleNum) : '',
                family_medical_deductible: deductibleNum > 0 ? formatCurrencyWithCommas(deductibleNum) : '',
                individual_medical_moop: moopNum > 0 ? formatCurrencyWithCommas(moopNum) : '',
                family_medical_moop: moopNum > 0 ? formatCurrencyWithCommas(moopNum) : '',
                deductible_numerical: deductibleNum,
                max_out_of_pocket_numerical: moopNum,
                preventitive_numerical: 0,
                primary_care_numerical: 0,
                specelist_numerical: 0,
                emergency_numerical: 0,
                urgent_care_numerical: 0,
                telemedicine: null,
                telemedicine_eligible: false,
                gated: false,
                hsa_eligible: false,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ...({
                    isCurrentPlan: true,
                    deductibles: {
                        individual_medical: deductibleNum || undefined,
                        family_medical: deductibleNum || undefined,
                        individual_drug: deductibleNum || undefined,
                        family_drug: deductibleNum || undefined,
                    },
                    moop: {
                        individual: moopNum || undefined,
                        family: moopNum || undefined,
                    },
                } as any),
            };

            setComparePlans((prev) => {
                const withoutCurrent = prev.filter((p) => !(p as any).isCurrentPlan && p.id !== 'current-plan');
                if (withoutCurrent.length >= 3) return prev;
                return [...withoutCurrent, currentPlan];
            });

            setIsCurrentPlanModalOpen(false);
        },
        [parseMoney],
    );

    const hasTooManyFilters = useMemo(() => {
        if (filteredPlans.length > 0) return false;
        if (filters.metal.length === 0 || filters.carriers.length === 0 || filters.planMarket.length === 0) return true;

        let activeFiltersCount = 0;
        if (filters.metal.length > 0 && filters.metal.length < 5) activeFiltersCount++;
        if (filters.planMarket.length > 0 && filters.planMarket.length < 2) activeFiltersCount++;
        if (filters.carriers.length > 0) activeFiltersCount++;
        if (filters.features.length > 0) activeFiltersCount++;
        if (filters.premiumMin !== undefined || filters.premiumMax !== undefined) activeFiltersCount++;
        if (filters.maxOutOfPocketMin !== undefined || filters.maxOutOfPocketMax !== undefined) activeFiltersCount++;

        return activeFiltersCount > 2;
    }, [filteredPlans.length, filters]);

    const filtersKey = useMemo(() => {
        return JSON.stringify({
            metal: [...filters.metal].sort(),
            planMarket: [...filters.planMarket].sort(),
            carriers: [...filters.carriers].sort(),
            features: [...filters.features].sort(),
            premiumMin: filters.premiumMin,
            premiumMax: filters.premiumMax,
            deductibleMin: filters.deductibleMin,
            deductibleMax: filters.deductibleMax,
            maxOutOfPocketMin: filters.maxOutOfPocketMin,
            maxOutOfPocketMax: filters.maxOutOfPocketMax,
        });
    }, [filters]);

    const getFilteredPlansExcluding = useMemo(() => {
        return (excludeMetal?: boolean, excludeMarket?: boolean) => {
            return plans.filter((plan) => {
                if (!excludeMetal && filters.metal.length > 0) {
                    const planLevel = plan.level.toLowerCase();
                    const matchesMetal = filters.metal.some((metal) => planLevel.includes(metal.toLowerCase()));
                    if (!matchesMetal) return false;
                }

                if (!excludeMarket && filters.planMarket.length > 0) {
                    const isOnExchange = plan.on_market === true;
                    const isOffExchange = plan.off_market === true;
                    const matchesMarket = filters.planMarket.some((market) => {
                        if (market === 'On-Exchange') return isOnExchange;
                        if (market === 'Off-Exchange') return isOffExchange;
                        return false;
                    });
                    if (!matchesMarket) return false;
                }

                if (filters.carriers.length > 0) {
                    const planCarrierName = plan.carrier.name.trim();
                    const matchesCarrier = filters.carriers.some((carrier) => {
                        const filterCarrierName = carrier.trim();
                        return planCarrierName.toLowerCase() === filterCarrierName.toLowerCase();
                    });
                    if (!matchesCarrier) return false;
                }

                if (filters.features.length > 0) {
                    if (filters.features.includes('HSA Eligible') && !plan.hsa_eligible) return false;
                    if (filters.features.includes('Specialist Without Referral') && plan.gated === true) return false;
                }

                if (filters.premiumMin !== undefined || filters.premiumMax !== undefined) {
                    if (!plan.premium) return false;

                    const premiumStr = String(plan.premium).trim();
                    const cleanedPremium = premiumStr.replace(/[^0-9.]/g, '');
                    const planPremium = parseFloat(cleanedPremium);
                    if (isNaN(planPremium)) return false;

                    if (filters.premiumMin !== undefined && planPremium < filters.premiumMin) return false;
                    if (filters.premiumMax !== undefined && planPremium > filters.premiumMax) return false;
                }

                if (filters.deductibleMin !== undefined || filters.deductibleMax !== undefined) {
                    const planDeductible = plan.deductible_numerical;
                    if (planDeductible === undefined || planDeductible === null || isNaN(planDeductible)) return false;
                    if (filters.deductibleMin !== undefined && planDeductible < filters.deductibleMin) return false;
                    if (filters.deductibleMax !== undefined && planDeductible > filters.deductibleMax) return false;
                }

                if (filters.maxOutOfPocketMin !== undefined || filters.maxOutOfPocketMax !== undefined) {
                    const planMaxOutOfPocket = plan.max_out_of_pocket_numerical;
                    if (planMaxOutOfPocket === undefined || planMaxOutOfPocket === null || isNaN(planMaxOutOfPocket)) return false;
                    if (filters.maxOutOfPocketMin !== undefined && planMaxOutOfPocket < filters.maxOutOfPocketMin) return false;
                    if (filters.maxOutOfPocketMax !== undefined && planMaxOutOfPocket > filters.maxOutOfPocketMax) return false;
                }

                return true;
            });
        };
    }, [plans, filtersKey]);

    const plansByMetalLevel = useMemo(() => {
        const counts = { Bronze: 0, Silver: 0, Gold: 0, Platinum: 0, Catastrophic: 0 };
        const filtered = getFilteredPlansExcluding(true, false);

        filtered.forEach((plan) => {
            const level = plan.level.toLowerCase();
            if (level.includes('bronze')) counts.Bronze++;
            else if (level.includes('silver')) counts.Silver++;
            else if (level.includes('gold')) counts.Gold++;
            else if (level.includes('platinum')) counts.Platinum++;
            else if (level.includes('catastrophic')) counts.Catastrophic++;
        });

        return counts;
    }, [getFilteredPlansExcluding]);

    const plansByExchange = useMemo(() => {
        const filtered = getFilteredPlansExcluding(false, true);
        let onExchange = 0;
        let offExchange = 0;
        filtered.forEach((plan) => {
            if (plan.on_market === true) onExchange++;
            if (plan.off_market === true) offExchange++;
        });
        return { onExchange, offExchange };
    }, [getFilteredPlansExcluding]);

    const handleChangePageSize = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        const newSize = Number(e.target.value) || 10;
        setPageSize(newSize);
        setPage(1);
    }, []);

    const handlePrevPage = useCallback(() => {
        setPage((prev) => Math.max(1, prev - 1));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const handleNextPage = useCallback(() => {
        setPage((prev) => Math.min(totalPages, prev + 1));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [totalPages]);

    const handleShowMore = useCallback(() => {
        setPage(1);
        setPageSize((prev) => Math.min(prev + 10, 50));
    }, []);

    const handleMetalFilterToggle = useCallback((metal: string) => {
        const isSelected = filters.metal.includes(metal);
        const nextMetal = isSelected ? filters.metal.filter((m) => m !== metal) : [...filters.metal, metal];
        const fixedMetal = nextMetal.length > 0 ? nextMetal : DEFAULT_METAL;
        const next = { ...filters, metal: fixedMetal };
        setFilters(next);
        writeFiltersToUrl(next);
        setPage(1);
    }, [DEFAULT_METAL, filters, writeFiltersToUrl]);

    const handleExchangeFilterToggle = useCallback((exchange: string) => {
        const isSelected = filters.planMarket.includes(exchange);
        const nextMarket = isSelected ? filters.planMarket.filter((m) => m !== exchange) : [...filters.planMarket, exchange];
        const fixedMarket = nextMarket.length > 0 ? nextMarket : DEFAULT_PLAN_MARKET;
        const next = { ...filters, planMarket: fixedMarket };
        setFilters(next);
        writeFiltersToUrl(next);
        setPage(1);
    }, [DEFAULT_PLAN_MARKET, filters, writeFiltersToUrl]);

    const handleSortByChange = useCallback((value: SortOption) => {
        if (isHydratingFromUrlRef.current) return;
        setSortBy(value);
        const params = new URLSearchParams(searchParams);
        if (!value || value === 'lowest-premium') params.delete('sortBy');
        else params.set('sortBy', value);
        if (params.toString() !== searchParams.toString()) {
            setSearchParams(params, { replace: true, preventScrollReset: true });
        }
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        const handleOpenFiltersModal = () => setIsFiltersModalOpen(true);
        window.addEventListener('openFiltersModal', handleOpenFiltersModal as EventListener);
        return () => window.removeEventListener('openFiltersModal', handleOpenFiltersModal as EventListener);
    }, []);

    const handleBackToMap = useCallback(() => {
        navigate(buildShopPlansStateRootPath(), { replace: true });
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            window.dispatchEvent(new CustomEvent('switchToStateCountyTab'));
        }, 100);
    }, [navigate]);

    return {
        // props echoes
        isStateCountrySearch,
        familyAges,
        activeTab,

        // derived
        filteredPlans,
        paginatedPlans,
        totalPages,
        summaryData,
        hasTooManyFilters,
        plansByMetalLevel,
        plansByExchange,

        // state
        page,
        pageSize,
        sortBy,
        filters,
        comparePlans,
        isFiltersModalOpen,
        isCompareModalOpen,
        isCurrentPlanModalOpen,
        isCompareCurrentPlanLimitModalOpen,
        isComparePlansExpanded,
        isEmployerContributionExpanded,
        employerContributionInput,
        employerContributionAmount,

        // setters
        setIsFiltersModalOpen,
        setIsCompareModalOpen,
        setIsCurrentPlanModalOpen,
        setIsCompareCurrentPlanLimitModalOpen,
        setIsComparePlansExpanded,
        setIsEmployerContributionExpanded,
        setEmployerContributionInput,
        setEmployerContributionAmount,
        setPage,

        // actions
        parseMoney,
        formatMoneyInput,
        formatMoneyFixed2,
        toTitleCase,

        handleBackToMap,
        handleFiltersPanelChange,
        handleToggleComparePlan,
        handleRemoveComparePlan,
        handleAddCurrentPlanToCompare,
        handleChangePageSize,
        handlePrevPage,
        handleNextPage,
        handleShowMore,
        handleMetalFilterToggle,
        handleExchangeFilterToggle,
        handleSortByChange,
    };
}

